import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const StatusBadge = ({ status }) => {
  const map = {
    active: 'bg-success/10 text-success',
    suspended: 'bg-error/10 text-error',
    influencer: 'bg-primary/10 text-primary',
    brand: 'bg-secondary/10 text-secondary',
    admin: 'bg-accent/10 text-accent',
  };
  return (
    <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${map?.[status] || 'bg-muted text-muted-foreground'}`}>
      {status}
    </span>
  );
};

const UserProfileModal = ({ user, onClose, onAdjustScore, onToggleStatus }) => {
  const [scoreInput, setScoreInput] = useState(user?.score || 0);
  const [adjusting, setAdjusting] = useState(false);

  const handleAdjust = async () => {
    setAdjusting(true);
    await onAdjustScore(user?.id, parseInt(scoreInput));
    setAdjusting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[200] flex items-center justify-center p-4">
      <div className="bg-card rounded-2xl border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h3 className="text-base font-semibold text-foreground">User Profile</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground">
            <Icon name="X" size={16} />
          </button>
        </div>
        <div className="p-5 space-y-5">
          {/* Profile header */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
              <Icon name="User" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <p className="text-base font-semibold text-foreground">{user?.name}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <StatusBadge status={user?.role} />
                <StatusBadge status={user?.status} />
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'SC Score', value: user?.score, icon: 'Star', color: 'text-primary' },
              { label: 'Submissions', value: user?.submissions, icon: 'FileText', color: 'text-secondary' },
              { label: 'Earned', value: user?.earned, icon: 'DollarSign', color: 'text-success' },
            ]?.map((s, i) => (
              <div key={i} className="bg-muted/50 rounded-lg p-3 text-center">
                <Icon name={s?.icon} size={16} className={`${s?.color} mx-auto mb-1`} />
                <p className="text-lg font-bold text-foreground">{s?.value}</p>
                <p className="text-xs text-muted-foreground">{s?.label}</p>
              </div>
            ))}
          </div>

          {/* Social handles */}
          {user?.socialHandles && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Social Handles</p>
              <div className="space-y-1">
                {Object.entries(user?.socialHandles || {})?.map(([platform, handle]) => (
                  <div key={platform} className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground capitalize w-20">{platform}:</span>
                    <span className="text-foreground">{handle || '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Adjust score */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Adjust SocialCents Score</p>
            <div className="flex gap-2">
              <Input
                type="number"
                value={scoreInput}
                onChange={(e) => setScoreInput(e?.target?.value)}
                className="flex-1"
              />
              <Button variant="default" size="sm" onClick={handleAdjust} disabled={adjusting}>
                {adjusting ? 'Saving...' : 'Update'}
              </Button>
            </div>
          </div>

          {/* Recent transactions */}
          {user?.recentTransactions?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Recent Transactions</p>
              <div className="space-y-2">
                {user?.recentTransactions?.slice(0, 4)?.map((t, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-muted/40 rounded-lg">
                    <div>
                      <p className="text-xs font-medium text-foreground">{t?.campaign || 'Campaign'}</p>
                      <p className="text-xs text-muted-foreground">{t?.date}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-semibold text-foreground">${parseFloat(t?.amount || 0)?.toFixed(2)}</p>
                      <StatusBadge status={t?.status} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              variant={user?.status === 'active' ? 'outline' : 'default'}
              size="sm"
              iconName={user?.status === 'active' ? 'UserX' : 'UserCheck'}
              onClick={() => { onToggleStatus(user?.id, user?.is_active); onClose(); }}
              className="flex-1"
            >
              {user?.status === 'active' ? 'Suspend Account' : 'Activate Account'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UserManagement = ({ currentAdminId }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [togglingUser, setTogglingUser] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('user_profiles')
        ?.select('id, full_name, email, role, is_active, created_at, profile_verified')
        ?.order('created_at', { ascending: false });
      if (!error && data) {
        const withScores = await Promise.all(
          data?.map(async (u) => {
            const { data: scoreData } = await supabase?.from('scores')?.select('total_score, total_earned, transaction_count, wallet_balance')?.eq('user_id', u?.id)?.maybeSingle();
            const { data: txData } = await supabase?.from('transactions')?.select('id, amount, status, created_at, campaigns(title)')?.eq('influencer_id', u?.id)?.order('created_at', { ascending: false })?.limit(4);
            return {
              id: u?.id,
              name: u?.full_name || u?.email?.split('@')?.[0] || 'User',
              email: u?.email || '',
              role: u?.role || 'influencer',
              score: scoreData?.total_score || 0,
              submissions: scoreData?.transaction_count || 0,
              earned: `$${parseFloat(scoreData?.total_earned || 0)?.toFixed(0)}`,
              walletBalance: `$${parseFloat(scoreData?.wallet_balance || 0)?.toFixed(2)}`,
              status: u?.is_active ? 'active' : 'suspended',
              is_active: u?.is_active,
              joinDate: new Date(u?.created_at)?.toLocaleDateString(),
              verified: u?.profile_verified,
              recentTransactions: (txData || [])?.map(t => ({
                campaign: t?.campaigns?.title || 'Campaign',
                amount: t?.amount,
                status: t?.status,
                date: new Date(t?.created_at)?.toLocaleDateString(),
              })),
            };
          })
        );
        setUsers(withScores);
      }
    } catch (err) {
      console.error('[UserManagement] loadUsers error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleToggleStatus = async (userId, currentStatus) => {
    setTogglingUser(userId);
    try {
      const newStatus = !currentStatus;
      const { error } = await supabase?.from('user_profiles')?.update({ is_active: newStatus })?.eq('id', userId);
      if (!error) {
        setUsers(prev => prev?.map(u => u?.id === userId ? { ...u, is_active: newStatus, status: newStatus ? 'active' : 'suspended' } : u));
        await supabase?.from('moderation_logs')?.insert({
          moderator_id: currentAdminId,
          target_user_id: userId,
          action: 'reviewed',
          notes: newStatus ? 'Account activated by admin' : 'Account suspended by admin',
        });
      }
    } catch (err) {
      console.error('[UserManagement] handleToggleStatus error:', err);
    }
    setTogglingUser(null);
  };

  const handleAdjustScore = async (userId, newScore) => {
    try {
      const { error } = await supabase?.from('scores')?.update({ total_score: newScore })?.eq('user_id', userId);
      if (!error) {
        setUsers(prev => prev?.map(u => u?.id === userId ? { ...u, score: newScore } : u));
        if (selectedUser?.id === userId) setSelectedUser(prev => ({ ...prev, score: newScore }));
        await supabase?.from('moderation_logs')?.insert({
          moderator_id: currentAdminId,
          target_user_id: userId,
          action: 'reviewed',
          notes: `Score adjusted to ${newScore} by admin`,
        });
      }
    } catch (err) {
      console.error('[UserManagement] handleAdjustScore error:', err);
    }
  };

  const filteredUsers = users?.filter(u => {
    const matchSearch = u?.name?.toLowerCase()?.includes(search?.toLowerCase()) || u?.email?.toLowerCase()?.includes(search?.toLowerCase());
    const matchRole = roleFilter === 'all' || u?.role === roleFilter;
    const matchStatus = statusFilter === 'all' || u?.status === statusFilter;
    return matchSearch && matchRole && matchStatus;
  });

  const handleExport = () => {
    const headers = ['Name', 'Email', 'Role', 'Score', 'Submissions', 'Earned', 'Status', 'Join Date'];
    const rows = filteredUsers?.map(u => [u?.name, u?.email, u?.role, u?.score, u?.submissions, u?.earned, u?.status, u?.joinDate]);
    const csv = [headers, ...rows]?.map(r => r?.map(c => `"${String(c)?.replace(/"/g, '""')}"`)?.join(','))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `users_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    document.body?.appendChild(a); a?.click(); document.body?.removeChild(a); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div className="bg-card rounded-xl border border-border p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <div>
            <h3 className="text-base font-semibold text-foreground">All Users</h3>
            <p className="text-sm text-muted-foreground">{filteredUsers?.length} of {users?.length} users</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-48">
              <Input type="search" placeholder="Search users..." value={search} onChange={(e) => setSearch(e?.target?.value)} />
            </div>
            <div className="w-36">
              <Select
                options={[{ value: 'all', label: 'All Roles' }, { value: 'influencer', label: 'Influencer' }, { value: 'brand', label: 'Brand' }, { value: 'admin', label: 'Admin' }]}
                value={roleFilter} onChange={setRoleFilter}
              />
            </div>
            <div className="w-36">
              <Select
                options={[{ value: 'all', label: 'All Status' }, { value: 'active', label: 'Active' }, { value: 'suspended', label: 'Suspended' }]}
                value={statusFilter} onChange={setStatusFilter}
              />
            </div>
            <Button variant="outline" size="sm" iconName="Download" iconPosition="left" onClick={handleExport}>Export</Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  {['User', 'Role', 'SC Score', 'Submissions', 'Earned', 'Wallet', 'Status', 'Joined', 'Actions']?.map(h => (
                    <th key={h} className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers?.map((u) => (
                  <tr key={u?.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon name="User" size={14} color="var(--color-primary)" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{u?.name}</p>
                          <p className="text-xs text-muted-foreground">{u?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3"><StatusBadge status={u?.role} /></td>
                    <td className="py-3 px-3"><span className="text-sm font-semibold text-primary">{u?.score}</span></td>
                    <td className="py-3 px-3"><span className="text-sm text-foreground">{u?.submissions}</span></td>
                    <td className="py-3 px-3"><span className="text-sm font-medium text-success">{u?.earned}</span></td>
                    <td className="py-3 px-3"><span className="text-sm text-foreground">{u?.walletBalance}</span></td>
                    <td className="py-3 px-3"><StatusBadge status={u?.status} /></td>
                    <td className="py-3 px-3"><span className="text-xs text-muted-foreground">{u?.joinDate}</span></td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setSelectedUser(u)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="View profile">
                          <Icon name="Eye" size={14} />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(u?.id, u?.is_active)}
                          disabled={togglingUser === u?.id}
                          className={`p-1.5 rounded-lg transition-colors ${u?.is_active ? 'hover:bg-error/10 text-muted-foreground hover:text-error' : 'hover:bg-success/10 text-muted-foreground hover:text-success'}`}
                          title={u?.is_active ? 'Suspend' : 'Activate'}
                        >
                          <Icon name={u?.is_active ? 'UserX' : 'UserCheck'} size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredUsers?.length === 0 && (
                  <tr><td colSpan={9} className="py-10 text-center text-sm text-muted-foreground">No users found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {selectedUser && (
        <UserProfileModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onAdjustScore={handleAdjustScore}
          onToggleStatus={handleToggleStatus}
        />
      )}
    </div>
  );
};

export default UserManagement;
